// src/pages/OrderOnTheWay/components/PaymentPendingCard.jsx
import React, { useState, useMemo } from "react";
import {
  calculateTotalPayment,
  calculateOrderSettlement,
  calculateSellerSettlement,
} from "../utils/calculationutil";
import {
  handleAssignJre,
  handleVerifyPayment,
  handleFinalPaymentForSeller,
} from "../services/paymentService";
import OrderTableWithAccordion from "./OrderTable";
import OtpVerificationModal from "./OtpVerificationModal";
import PayModal from "./PayModal";

const PaymentPendingCard = ({
  seller,
  group,
  loading,
  setAlertMessage,
  onPaymentVerified,
}) => {
  const [verifyModal, setVerifyModal] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const totalPayment = calculateTotalPayment(group.orders);
  const isJreAssigned = group.jreId && group.jreId !== "";

  const sellerSettlement = useMemo(() => {
    return calculateSellerSettlement(group.orders);
  }, [group.orders]);

  const orderSettlements = useMemo(() => {
    return group.orders.map((order) => calculateOrderSettlement(order));
  }, [group.orders]);

  const totalNetWt = orderSettlements.reduce((sum, s) => sum + s.TotalnetWt, 0);
  const totalGrossWt = orderSettlements.reduce(
    (sum, s) => sum + s.TotalgrossWt,
    0,
  );
  console.log(group);
  const effectiveSettlementId =
    group.settlementId ||
    group.sellerSettlementId ||
    group.orders?.[0]?.sellerSettlementId ||
    group.orders?.[0]?.settlementId ||
    null;

  const isOnWay = group.orders?.some(
    (o) => o.orderStatus === "paymentDispatchedToSeller",
  );

  const isSettled = Boolean(
    effectiveSettlementId || group.orders?.some((o) => o.sellerSettlementId),
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Handlers
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Called by PayModal on "Confirm Settlement".
   * Passes the rich settlement payload to handleFinalPaymentForSeller
   * which writes to Firestore (settlements + orderList top-level updates).
   */
  const onPayModalConfirm = async (settlementPayload) => {
    setLocalLoading(true);
    try {
      const result = await handleFinalPaymentForSeller(
        seller,
        group,
        setAlertMessage,
        settlementPayload,
      );
      setShowPayModal(false);
      setAlertMessage(
        `✅ Payment settled! Settlement ID: ${result.settlementId}`,
      );
    } catch (error) {
      setAlertMessage(`❌ ${error.message}`);
      throw error; // let PayModal show it too
    } finally {
      setLocalLoading(false);
    }
  };

  const onAssignJre = async () => {
    setLocalLoading(true);
    try {
      await handleAssignJre(seller, group, setAlertMessage);
    } catch (error) {
      setAlertMessage(`❌ ${error.message}`);
    }
    setLocalLoading(false);
  };

  const onVerifyPayment = async () => {
    setLocalLoading(true);
    try {
      const modalData = await handleVerifyPayment(
        seller,
        group,
        setAlertMessage,
      );
      if (modalData) setVerifyModal(modalData);
    } catch (error) {
      setAlertMessage(`❌ ${error.message}`);
    }
    setLocalLoading(false);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Styles
  // ──────────────────────────────────────────────────────────────────────────

  const cardStyle = {
    marginBottom: "24px",
    border: "2px solid #1f2937",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#ffffff",
    borderLeft: `4px solid ${isJreAssigned ? "#28a745" : "#6b7280"}`,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  const cardHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "16px",
  };

  const sellerInfoStyle = { flex: 1 };

  const sellerNameStyle = {
    margin: "0 0 8px",
    color: "#1f2937",
    fontSize: "20px",
    fontWeight: "bold",
  };

  const orderDetailsStyle = {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    color: "#6b7280",
    fontSize: "14px",
  };

  const jreAssignedStyle = { color: "#17a2b8", fontWeight: "bold" };

  const actionButtonsStyle = {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  };

  const btnBase = {
    border: "none",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "500",
    color: "white",
  };
  const buttonPrimaryStyle = { ...btnBase, backgroundColor: "#6366f1" };
  const buttonSuccessStyle = { ...btnBase, backgroundColor: "#28a745" };
  const buttonWarningStyle = { ...btnBase, backgroundColor: "#f59e0b" };
  const buttonDisabledStyle = { opacity: 0.6, cursor: "not-allowed" };
  // ──────────────────────────────────────────────────────────────────────────
  // Render action buttons based on workflow state
  // ──────────────────────────────────────────────────────────────────────────
  const renderActionButtons = () => {
    if (isOnWay) return null; // 🚚 On the Way — no actions

    if (!isSettled) {
      // Step 1: Pay seller first
      return (
        <button
          onClick={() => setShowPayModal(true)}
          disabled={localLoading}
          style={{
            ...buttonWarningStyle,
            ...(localLoading ? buttonDisabledStyle : {}),
          }}
        >
          {localLoading ? "Processing…" : "💳 Final Payment"}
        </button>
      );
    }
    if (!isJreAssigned) {
      // Step 2: Assign JRE
      return (
        <button
          onClick={onAssignJre}
          disabled={localLoading}
          style={{
            ...buttonPrimaryStyle,
            ...(localLoading ? buttonDisabledStyle : {}),
          }}
        >
          {localLoading ? "Assigning…" : "👤 Assign JRE"}
        </button>
      );
    }

    // Step 3: Verify JRE via OTP
    return (
      <button
        onClick={onVerifyPayment}
        disabled={localLoading}
        style={{
          ...buttonSuccessStyle,
          ...(localLoading ? buttonDisabledStyle : {}),
        }}
      >
        {localLoading ? "Sending OTP…" : "🔐 Verify JRE"}
      </button>
    );
  };

  return (
    <>
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <div style={sellerInfoStyle}>
            <h3 style={sellerNameStyle}>{seller}</h3>
            <div style={orderDetailsStyle}>
              <span>
                <strong>Total Amount:</strong> ₹{totalPayment.toFixed(2)}
              </span>
              <span>
                <strong>Orders:</strong> {group.orders.length}
              </span>
              <span>
                <strong>Net Wt:</strong> {totalNetWt.toFixed(3)}g
              </span>
              <span>
                <strong>Gross Wt:</strong> {totalGrossWt.toFixed(3)}g
              </span>
              <span>
                <strong>Seller Fine:</strong> ₹
                {sellerSettlement.fine.toFixed(2)}
              </span>

              {isJreAssigned && (
                <span style={jreAssignedStyle}>
                  <strong>Assigned JRE:</strong> {group.jreId}
                </span>
              )}

              {effectiveSettlementId && (
                <span
                  style={{
                    backgroundColor: "#e0e7ff",
                    color: "#3730a3",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    fontFamily: "monospace",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  🔖 <strong>Settlement ID:</strong> {effectiveSettlementId}
                </span>
              )}

              {isOnWay && (
                <span
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  🚚 <strong>Status:</strong> On the Way to Seller
                </span>
              )}

              {/* Workflow step indicator */}
              {!isOnWay && (
                <span
                  style={{
                    backgroundColor: isSettled
                      ? isJreAssigned
                        ? "#d1fae5"
                        : "#dbeafe"
                      : "#fef3c7",
                    color: isSettled
                      ? isJreAssigned
                        ? "#065f46"
                        : "#1e40af"
                      : "#92400e",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {!isSettled
                    ? ""
                    : !isJreAssigned
                      ? "Step 1: Assign JRE"
                      : "Step 2: Verify JRE"}
                </span>
              )}
            </div>
          </div>

          <div style={actionButtonsStyle}>{renderActionButtons()}</div>
        </div>

        <OrderTableWithAccordion
          orders={group.orders}
          showFineDetails={true}
          groupBy="seller"
        />
      </div>

      {/* Pay Modal */}
      {showPayModal && (
        <PayModal
          seller={seller}
          group={group}
          onClose={() => setShowPayModal(false)}
          onConfirm={onPayModalConfirm}
        />
      )}

      {/* JRE OTP Verification Modal */}
      {verifyModal && (
        <OtpVerificationModal
          modalData={verifyModal}
          onClose={() => setVerifyModal(null)}
          onVerify={(message) => {
            setAlertMessage(message);
            setVerifyModal(null);
          }}
        />
      )}
    </>
  );
};

export default PaymentPendingCard;
